import { useCallback, type Dispatch, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeftIcon } from "@phosphor-icons/react";
import { SOCIAL_LINKS } from "../constants/social_links";
import { register, verifyResetCode, type RegistrationDraft } from "./auth_api";
import type { AuthFlow, AuthScreen, FlowScreen } from "./auth_flow";
import { BACK_LINK_CLASS } from "./auth_fields";
import LoginForm from "./login_form";
import RegistrationForm from "./registration_form";
import CodeForm from "./code_form";
import RecoveryForm from "./recovery_form";
import NewPasswordForm from "./new_password_form";

export type AuthReason =
	| "default"
	| "question"
	| "mentor"
	| "notes"
	| "support"
	| "course"
	| "copy-note";

const TAB_CLASS = [
	"flex-1 h-10 rounded-full border-0 cursor-pointer text-base font-medium bg-transparent text-auth-sub",
	"aria-selected:bg-auth-card aria-selected:text-auth-text aria-selected:shadow-sm",
].join(" ");

const STEP_TITLES: Record<Exclude<FlowScreen, AuthScreen>, string> = {
	code: "auth.code-title",
	"recovery-code": "auth.code-title",
	recovery: "auth.recovery-title",
	"new-password": "auth.new-password-title",
};

const REASON_TITLES: Partial<Record<AuthReason, string>> = {
	question: "auth.title-question",
	mentor: "auth.title-mentor",
	notes: "auth.title-notes",
	support: "auth.title-support",
	course: "auth.title-course",
	"copy-note": "auth.title-copy-note",
};

export const titleKey = (screen: FlowScreen, reason: AuthReason): string => {
	if (screen !== "login" && screen !== "registration") return STEP_TITLES[screen];
	return (
		REASON_TITLES[reason] ??
		(screen === "login" ? "auth.headline-login" : "auth.headline-registration")
	);
};

interface Props {
	flow: AuthFlow;
	setFlow: Dispatch<SetStateAction<AuthFlow>>;
	reason: AuthReason;
	/** Where Google brings the reader back to. */
	returnTo: string;
	onSignedIn: (token: string) => void;
	/** Called before leaving for Google (the resume marker is written here). */
	onLeaveForGoogle?: () => void;
	/** The page names the tab above the card itself; the tabs already show which one is open. */
	hideTabTitle?: boolean;
	/** The page keeps the tab in its address; the modal just switches. */
	onTab?: (screen: AuthScreen) => void;
}

/** The sign-in card itself: title, tabs and every step; the modal and the /auth page wrap it. */
const AuthCard = ({
	flow,
	setFlow,
	reason,
	returnTo,
	onSignedIn,
	onLeaveForGoogle,
	hideTabTitle,
	onTab,
}: Props) => {
	const { t } = useTranslation();

	const go = (screen: FlowScreen) => setFlow(f => ({ ...f, screen }));
	const updateDraft = useCallback(
		(update: (d: RegistrationDraft) => RegistrationDraft) =>
			setFlow(f => ({ ...f, draft: update(f.draft) })),
		[setFlow],
	);

	const { screen, draft, recovery } = flow;
	const title = t(titleKey(screen, reason));
	const isTabbed = screen === "login" || screen === "registration";
	const backToLogin = screen === "recovery" || screen === "new-password";
	const showTitle = !(hideTabTitle && isTabbed);

	return (
		<>
			{backToLogin && (
				<button type="button" className={BACK_LINK_CLASS} onClick={() => go("login")}>
					<ArrowLeftIcon size={14} /> {t("auth.back")}
				</button>
			)}
			{showTitle && (
				<h2
					id="auth-card-title"
					data-testid="auth-title"
					className="m-0 mb-5 pr-8 font-display text-2xl font-bold leading-[1.2]"
				>
					{title}
				</h2>
			)}
			{isTabbed && (
				<div
					role="tablist"
					data-testid="auth-tabs"
					className="flex gap-1 p-1 mb-5 rounded-full bg-auth-bg"
				>
					{(["login", "registration"] as const).map(s => (
						<button
							key={s}
							type="button"
							role="tab"
							aria-selected={screen === s}
							data-testid={s === "login" ? "auth-login-tab" : "auth-registration-tab"}
							className={TAB_CLASS}
							onClick={() => (onTab ? onTab(s) : go(s))}
						>
							{t(s === "login" ? "auth.tab-login" : "auth.tab-registration")}
						</button>
					))}
				</div>
			)}
			{screen === "login" && (
				<LoginForm
					onSignedIn={onSignedIn}
					returnTo={returnTo}
					onLeaveForGoogle={onLeaveForGoogle}
					onForgot={email =>
						setFlow(f => ({ ...f, screen: "recovery", recovery: { ...f.recovery, email } }))
					}
				/>
			)}
			{/* Kept mounted on the code screen: the place picker holds what was typed and "Back" must find it. */}
			{(screen === "registration" || screen === "code") && (
				<div hidden={screen === "code"}>
					<RegistrationForm
						draft={draft}
						updateDraft={updateDraft}
						onCodeSent={() => go("code")}
						returnTo={returnTo}
						onLeaveForGoogle={onLeaveForGoogle}
					/>
				</div>
			)}
			{screen === "code" && (
				<CodeForm
					email={draft.email}
					step="register"
					verify={async code => onSignedIn(await register(draft, code))}
					onBack={() => go("registration")}
				/>
			)}
			{screen === "recovery" && (
				<RecoveryForm
					initialEmail={recovery.email}
					onCodeSent={(email, token) =>
						setFlow(f => ({ ...f, screen: "recovery-code", recovery: { email, token } }))
					}
				/>
			)}
			{screen === "recovery-code" && (
				<CodeForm
					email={recovery.email}
					step="verify-reset"
					verify={async code => {
						await verifyResetCode({ email: recovery.email, code, token: recovery.token });
						go("new-password");
					}}
					onBack={() => go("recovery")}
				/>
			)}
			{screen === "new-password" && (
				<NewPasswordForm email={recovery.email} token={recovery.token} onSignedIn={onSignedIn} />
			)}
			{/* Came for support: a way out for whoever cannot sign in at all. */}
			{reason === "support" && (
				<a
					href={SOCIAL_LINKS.TELEGRAM}
					target="_blank"
					rel="noopener noreferrer"
					className="block mt-5 text-center text-sm text-auth-sub underline underline-offset-2 hover:text-auth-text"
				>
					{t("auth.support-telegram")}
				</a>
			)}
		</>
	);
};

export default AuthCard;
